import React from 'react';

import ActionButton from 'components/ActionButton';
import Snackbar from 'components/DesignSystem/Snackbar';
import {
  useDeleteAutomaticPreferenceMutation,
  useGetAutomaticPreferencesQuery,
} from 'services/api/products/flux';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import { useDispatch, useSelector } from 'react-redux';
import { AutomaticPreferenceType } from 'helper/products/flux';
import { SpatialSubdivision } from 'helper/flux/automatic';
import useAutomaticOptions from 'utils/hooks/flux/useAutomaticOptions';
import useFluxAutomaticFilterSelector from 'utils/hooks/flux/automatic/filterSelector';
import {
  FluxAutomaticLayoutTabs,
  selectFluxAutomaticLayoutCurrentTab,
} from 'redux/reducers/fluxAutomaticLayoutReducer';
import {
  setMLT,
  setAcomph,
  setPastSimulations,
  setAutomaticStudyPreferenceScenarios,
} from 'redux/reducers/fluxAutomaticControlPanelReducer';

import './index.scss';

const tabToPreferenceType = new Map<FluxAutomaticLayoutTabs, AutomaticPreferenceType>([
  ['daily', 'diaria'],
  ['monthly', 'mensal'],
  ['revision', 'revisao'],
]);

export default function SavedPreferences({ onApplied }: { onApplied: () => void }) {
  const dispatch = useDispatch();
  const currentTab: FluxAutomaticLayoutTabs = useSelector(selectFluxAutomaticLayoutCurrentTab);
  const type = tabToPreferenceType.get(currentTab) || 'diaria';
  const { data, error } = useGetAutomaticPreferencesQuery(type);
  const [
    deletePreference,
    { error: deleteError, isLoading: deleteLoading },
  ] = useDeleteAutomaticPreferenceMutation();
  const { subdivisions } = useAutomaticOptions();
  const { setFilterWithinDivision } = useFluxAutomaticFilterSelector();

  useHandleRequestErrors(error as ResponseError);
  useHandleRequestErrors(deleteError as ResponseError);

  const preferences = data ?? [];

  // Mesma sequência do `Preference` do desktop: a preferência restaura filtro espacial,
  // cenários, ACOMPH/MLT e simulações antigas de uma vez.
  const apply = (preference: typeof preferences[number]) => {
    const subOption = subdivisions[`${preference.spatialSubDivisionKey}s`]
      ?.find((option) => option.label === preference.spatialSubDivisionValue);
    setFilterWithinDivision(subOption, `${preference.spatialSubDivisionKey}s` as SpatialSubdivision);
    dispatch(setAutomaticStudyPreferenceScenarios(preference));
    dispatch(setAcomph(preference.acomph));
    dispatch(setMLT(preference.mlt));
    dispatch(setPastSimulations(preference));
    onApplied();
  };

  if (preferences.length === 0) {
    return <Snackbar className="mt-2">Nenhuma preferência salva.</Snackbar>;
  }

  return (
    <>
      {preferences.map((preference) => (
        <div className="flux-automatic-saved-preference" key={preference.id}>
          <button
            type="button"
            className="flux-automatic-saved-preference-apply"
            onClick={() => apply(preference)}
          >
            {preference.name}
          </button>
          <ActionButton
            loading={deleteLoading}
            onClick={() => deletePreference(preference.id)}
            icon="trash"
            type="tertiary"
            small
          />
        </div>
      ))}
    </>
  );
}
